/**
 * Canonical list of every secret HOTTR.app needs in production.
 *
 * Shared between the /admin/secrets page (presentation) and the
 * `secrets-manager` edge function (persistence + testing). The
 * `target` field controls where a Save lands:
 *
 *   - 'netlify_env'    → Netlify site environment variable (build-time vars,
 *                        anything `VITE_*` baked into the client bundle).
 *                        Saved via the Netlify API; takes effect on next deploy.
 *   - 'supabase_secret' → Supabase Edge Function secret (server-only).
 *                        Saved via the Supabase Management API; takes effect
 *                        immediately for all edge functions in the project.
 *   - 'info'           → Read-only marker, no save button (e.g. webhook URLs
 *                        users need to paste into Stripe/Persona dashboards).
 */

export type SecretTarget = 'netlify_env' | 'supabase_secret' | 'info';

export type SecretTest =
  | 'length'      // pass if value length ≥ minLength
  | 'url'         // pass if valid URL with https://
  | 'stripe_key'  // calls Stripe API to verify
  | 'persona_key' // calls Persona API to verify
  | 'vapid_pub'   // 87-char base64url
  | 'vapid_priv'  // 43-char base64url
  | 'noop';       // info rows; always pass

export interface SecretSpec {
  /** Identifier, e.g. `VITE_SUPABASE_URL`. Used as both env name and the dashboard label. */
  key: string;
  /** Where to write it. */
  target: SecretTarget;
  /** Which roadmap tier this unlocks. Drives section grouping in the UI. */
  tier:
    | 'core'
    | 'mapbox'
    | 'persona'
    | 'push'
    | 'stripe'
    | 'hottie'
    | 'cron'
    | 'admin-bootstrap';
  /** One-line description rendered above the input. */
  description: string;
  /** Optional doc URL — where to obtain this secret. */
  docsUrl?: string;
  /** Validation pre-test. */
  test: SecretTest;
  /** For `test: 'length'` — minimum acceptable length. */
  minLength?: number;
  /** When true, the row is hidden until the bootstrap secrets are set. */
  bootstrapRequires?: 'netlify' | 'supabase';
  /** Static value (info rows). */
  staticValue?: string;
}

export const SECRETS_REGISTRY: SecretSpec[] = [
  // ── Core Supabase + Mapbox (Netlify env, build-time) ──────────────────
  {
    key: 'VITE_SUPABASE_URL',
    target: 'netlify_env',
    tier: 'core',
    description: 'Supabase project URL — front-end + edge functions read this.',
    docsUrl: 'https://supabase.com/dashboard/project/_/settings/api',
    test: 'url',
  },
  {
    key: 'VITE_SUPABASE_ANON_KEY',
    target: 'netlify_env',
    tier: 'core',
    description: 'Supabase anon (publishable) JWT — safe to ship to the browser.',
    docsUrl: 'https://supabase.com/dashboard/project/_/settings/api',
    test: 'length',
    minLength: 100,
  },
  {
    key: 'VITE_MAPBOX_TOKEN',
    target: 'netlify_env',
    tier: 'mapbox',
    description: 'Mapbox access token — restricts to your domain in the Mapbox UI.',
    docsUrl: 'https://account.mapbox.com/access-tokens/',
    test: 'length',
    minLength: 40,
  },

  // ── Persona (Tier 3 — Selfie verification) ───────────────────────────
  {
    key: 'VITE_PERSONA_TEMPLATE_ID',
    target: 'netlify_env',
    tier: 'persona',
    description: 'Persona inquiry template id (e.g. itmpl_xxx). Front-end loads the embedded client.',
    docsUrl: 'https://app.withpersona.com/dashboard/templates',
    test: 'length',
    minLength: 5,
  },
  {
    key: 'VITE_PERSONA_ENV',
    target: 'netlify_env',
    tier: 'persona',
    description: 'sandbox or production — which Persona environment the embed opens against.',
    test: 'length',
    minLength: 6,
  },
  {
    key: 'PERSONA_API_KEY',
    target: 'supabase_secret',
    tier: 'persona',
    description: 'Persona API key — server-only; used for verification status lookups.',
    docsUrl: 'https://app.withpersona.com/dashboard/api-keys',
    test: 'persona_key',
  },
  {
    key: 'PERSONA_WEBHOOK_SECRET',
    target: 'supabase_secret',
    tier: 'persona',
    description: 'Persona webhook signing secret — verifies inbound webhook authenticity.',
    docsUrl: 'https://app.withpersona.com/dashboard/webhooks',
    test: 'length',
    minLength: 20,
  },
  {
    key: 'PERSONA_WEBHOOK_URL',
    target: 'info',
    tier: 'persona',
    description: 'Paste THIS URL into Persona Dashboard → Webhooks → New endpoint.',
    test: 'noop',
    staticValue: 'https://brnkdtlgzewxlqjiotwc.supabase.co/functions/v1/persona-webhook',
  },

  // ── Stripe billing (Tier 5) ──────────────────────────────────────────
  {
    key: 'VITE_STRIPE_PUBLISHABLE_KEY',
    target: 'netlify_env',
    tier: 'stripe',
    description: 'Stripe publishable key (pk_live_… or pk_test_…). Front-end may use for future Stripe.js features.',
    docsUrl: 'https://dashboard.stripe.com/apikeys',
    test: 'length',
    minLength: 20,
  },
  {
    key: 'VITE_STRIPE_PREMIUM_PRICE_ID',
    target: 'netlify_env',
    tier: 'stripe',
    description: 'Stripe Price ID for the A$24/month Premium plan.',
    docsUrl: 'https://dashboard.stripe.com/products',
    test: 'length',
    minLength: 5,
  },
  {
    key: 'VITE_STRIPE_VIP_PRICE_ID',
    target: 'netlify_env',
    tier: 'stripe',
    description: 'Stripe Price ID for the A$49/month VIP plan.',
    docsUrl: 'https://dashboard.stripe.com/products',
    test: 'length',
    minLength: 5,
  },
  {
    key: 'STRIPE_SECRET_KEY',
    target: 'supabase_secret',
    tier: 'stripe',
    description: 'Stripe secret key (sk_live_… / sk_test_…) — used by the checkout/portal/webhook edge functions.',
    docsUrl: 'https://dashboard.stripe.com/apikeys',
    test: 'stripe_key',
  },
  {
    key: 'STRIPE_WEBHOOK_SECRET',
    target: 'supabase_secret',
    tier: 'stripe',
    description: 'Webhook signing secret (whsec_…) from the Stripe webhook config.',
    docsUrl: 'https://dashboard.stripe.com/webhooks',
    test: 'length',
    minLength: 20,
  },
  {
    key: 'STRIPE_PREMIUM_PRICE_ID',
    target: 'supabase_secret',
    tier: 'stripe',
    description: 'Same Premium price id — server-side copy used by the checkout function.',
    test: 'length',
    minLength: 5,
  },
  {
    key: 'STRIPE_VIP_PRICE_ID',
    target: 'supabase_secret',
    tier: 'stripe',
    description: 'Same VIP price id — server-side copy used by the checkout function.',
    test: 'length',
    minLength: 5,
  },
  {
    key: 'STRIPE_WEBHOOK_URL',
    target: 'info',
    tier: 'stripe',
    description: 'Paste THIS URL into Stripe Dashboard → Developers → Webhooks. Listen for: customer.subscription.created/updated/deleted, checkout.session.completed.',
    test: 'noop',
    staticValue: 'https://brnkdtlgzewxlqjiotwc.supabase.co/functions/v1/stripe-webhook',
  },

  // ── Web Push (Tier 4) ────────────────────────────────────────────────
  {
    key: 'VITE_VAPID_PUBLIC_KEY',
    target: 'netlify_env',
    tier: 'push',
    description: 'VAPID public key (87 chars, base64url). Generate with `npx web-push generate-vapid-keys`.',
    test: 'vapid_pub',
  },
  {
    key: 'VAPID_PRIVATE_KEY',
    target: 'supabase_secret',
    tier: 'push',
    description: 'VAPID private key (43 chars, base64url) — paired with the public key above.',
    test: 'vapid_priv',
  },
  {
    key: 'VAPID_SUBJECT',
    target: 'supabase_secret',
    tier: 'push',
    description: 'Contact mailto: URL for push registrars (e.g. mailto:hello@hottr.app).',
    test: 'length',
    minLength: 7,
  },

  // ── Hottie speak (LLM hop is mate-api, not a local Grok/Hermes client) ──
  {
    key: 'DEEPGRAM_API_KEY',
    target: 'supabase_secret',
    tier: 'hottie',
    description: 'Deepgram API key — Flux TTS for experimental Hottie speak. Server-only; never VITE_*.',
    docsUrl: 'https://console.deepgram.com',
    test: 'length',
    minLength: 20,
  },

  // ── Cron shared secret (push-dispatch, hottie-push, hottie-context-condense) ──
  {
    key: 'HOTTIE_CRON_SECRET',
    target: 'supabase_secret',
    tier: 'cron',
    description: 'Random 32+ char string. Required header on internal cron-driven endpoints.',
    test: 'length',
    minLength: 16,
  },

  // ── Bootstrap (read by secrets-manager itself; set once, manually) ───
  {
    key: 'NETLIFY_API_TOKEN',
    target: 'supabase_secret',
    tier: 'admin-bootstrap',
    description: 'Netlify personal access token used by secrets-manager to write env vars. Set ONCE manually via supabase CLI.',
    docsUrl: 'https://app.netlify.com/user/applications#personal-access-tokens',
    test: 'length',
    minLength: 30,
  },
  {
    key: 'NETLIFY_SITE_ID',
    target: 'supabase_secret',
    tier: 'admin-bootstrap',
    description: 'Netlify site ID (UUID) — the site secrets-manager writes to.',
    docsUrl: 'https://app.netlify.com/sites/<your-site>/settings/general#site-information',
    test: 'length',
    minLength: 20,
  },
  {
    key: 'SUPABASE_MANAGEMENT_ACCESS_TOKEN',
    target: 'supabase_secret',
    tier: 'admin-bootstrap',
    description: 'Supabase Management API access token (sbp_…). Lets secrets-manager update its own project secrets.',
    docsUrl: 'https://supabase.com/dashboard/account/tokens',
    test: 'length',
    minLength: 30,
  },
  {
    key: 'SUPABASE_PROJECT_REF',
    target: 'supabase_secret',
    tier: 'admin-bootstrap',
    description: 'Supabase project ref (the URL subdomain — e.g. brnkdtlgzewxlqjiotwc).',
    test: 'length',
    minLength: 10,
  },
];

export const TIER_LABEL: Record<SecretSpec['tier'], string> = {
  core:                 'Core · Supabase',
  mapbox:               'Mapbox',
  persona:              'Persona · selfie verification',
  push:                 'Web Push',
  stripe:               'Stripe · billing',
  hottie:               'Hottie · speak',
  cron:                 'Cron secrets',
  'admin-bootstrap':    'Bootstrap · set once manually',
};

export const TIER_ORDER: SecretSpec['tier'][] = [
  'admin-bootstrap',
  'core',
  'mapbox',
  'cron',
  'persona',
  'push',
  'stripe',
  'hottie',
];
